import React, { useState } from 'react';
import { FileStatus } from '../../../../data/enums';
import { useTheme, IconButton, Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Chip, Tooltip  } from '@mui/material';
import { FilePresent as FilePresentIcon, HourglassTop as HourglassTopIcon, Delete as DeleteIcon, ErrorOutline as ErrorOutlineIcon, Close as CloseIcon, CloudDone as CloudDoneIcon } from '@mui/icons-material';
import { FileRejection, FileWithPath, FileError } from 'react-dropzone'
import Dropzone from '../dropzone/dropzone';

export interface IFileUploaderItemProps {
  File: IFileUpload;
  SetFileForUpload: (file: File | null) => void; // single-file model
  DeleteFile: () => void; // single-file model
  Height?: number;
  CanDelete?: boolean; // allow delete only in final snapshot lifecycle statuses
}
export default function FileUploaderItem(props: IFileUploaderItemProps) {
  
  const [openInvalidDialog, setOpenInvalidDialog] = React.useState(false);
  const theme = useTheme();
  const height = (props.Height) ? props.Height : 150;  
  const [rejectedFiles, setRejected] = useState<FileRejection[]>([])
  const [acceptedFiles, setFiles] = useState<FileWithPath[]>([])
  
  const onDrop = (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
    if (acceptedFiles?.length) {
      setFiles(previousFiles => [...previousFiles, ...acceptedFiles]);
      props.SetFileForUpload(acceptedFiles[0]);
    }
    if (fileRejections?.length) {
      setRejected(previousFiles => [...previousFiles, ...fileRejections]);
      setOpenInvalidDialog(true);
    }
  }; 

  const handleClose = ()=>{
    setOpenInvalidDialog(false);
    setRejected([]);
  }

  const handleRemoveFileClick = () => {
    props.SetFileForUpload(null);
  };  

  const handleDeleteFileClick = () => {
    props.DeleteFile();
  };  

  const rejected = rejectedFiles.map((file: FileRejection) => (  
    <div key={file.file.name}>
      <div className="invalidFile">{file.file.name}   
        <ul className="invalidReasonList">
          {file.errors.map((error: FileError) =>        
            (<li className="invalidReasonItem" key={error.code}>        
              {error.code == 'file-invalid-type' ? ('Invalid file type') : error.message} 
            </li>))}
        </ul>          
      </div>
    </div>
  ));

  const getFileSize = (fileSize: number | undefined) => {
    if (fileSize) return (fileSize / 1000000).toFixed(2);
    return 0;
  };
  
  return ( 
    <>

      {/* Rejected file dialog */}
      <Dialog aria-labelledby='alert-dialog-title' open={openInvalidDialog}>
        <DialogTitle sx={{ m: 0, p: 1 }}>Invalid File</DialogTitle>
        <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 4,
                top: 4,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
        <DialogContent dividers sx={{minHeight: "150px", lineHeight: 1, maxWidth: "100%", width: "500px"}}>
        {rejected}
        </DialogContent>
        <DialogActions sx={{ m: 0, p: 1 }}>
          <Button autoFocus onClick={handleClose}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* File outer container */}
      <Box sx={{ height: height}}>  

        {/* Empty - dotted outline, category label, browse or drag for file */}
        {props.File?.FileStatus === FileStatus.Empty &&         
          <Dropzone              
            file={props.File}
            multiple={false}
            // Accept CSV and SAV (SPSS) files. Using primary and fallback MIME types for .sav.
            accept={{
              'text/csv': ['.csv'],
              'application/x-spss-sav': ['.sav'],
              'application/octet-stream': ['.sav'] // fallback for browsers that default .sav
            }}
            fileRequirementsHelp='Please upload a single CSV or SAV (SPSS) file'
            fileRequirementsMessage='CSV/SAV'
            // title={props.File.Category}
            // height={height}
            onDrop={onDrop}
          />
        }

        {/* Collected - category label, file size, file name, bg color to visually distinguish  */}
        {props.File?.FileStatus === FileStatus.Collected && 
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              width:'100%',
              height:'100%', 
              border: '1px solid',
              borderColor: theme.palette.grey[400],
              borderRadius: 4
            }}>

            {/* Remove from staging */}
            <Tooltip title="Remove from staging.">
              <IconButton 
                aria-label="Remove from staging."
                color="primary"
                sx={{ position: 'absolute', top: 2, right: 2 }}
                onClick={() => handleRemoveFileClick()}>
                <DeleteIcon fontSize='small'/>
              </IconButton>
            </Tooltip>

            {/* Top */}
            <Box sx={{ alignSelf: 'center', flex: '1 1 auto', display: 'flex', flexDirection: 'column-reverse'}}>        
              <Chip label={'snapshot'} size='small' sx={{}} />
            </Box>

            {/* Middle */}
            <Box sx={{ alignSelf: 'center', textAlign: 'center', flex: '0 0 auto', py: 0 }}> 
              <Tooltip title={props.File.FileForUpload?.name}>
                <FilePresentIcon 
                  sx={{ mt: 1, color: theme.palette.text.secondary}}
                  fontSize='large'/>
              </Tooltip>
            </Box>

            {/* Bottom */}
            <Box sx={{ alignSelf: 'center', textAlign: 'center', flex: '1 1 auto' }}>
              <Typography component="h6" sx={{ color: theme.palette.text.secondary, fontSize: '.8rem' }}>
                {`${getFileSize(props.File.FileForUpload?.size)} MB`}<br/>
                Staged for upload.
              </Typography>
            </Box>
          </Box>
        }

        {/* Uploading - collected visual + loading progress bar (or % msg)  */}
        {props.File?.FileStatus === FileStatus.Uploading && 
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              width:'100%',
              height:'100%', 
              border: '1px solid',
              borderColor: theme.palette.grey[400],
              borderRadius: 4
            }}>

            {/* Top */}
            <Box sx={{ alignSelf: 'center', flex: '1 1 auto', display: 'flex', flexDirection: 'column-reverse', pb: 1 }}>        
              <Chip label={'snapshot'} size='small' sx={{}} />
            </Box>

            {/* Middle */}
            <Box sx={{ alignSelf: 'center', textAlign: 'center', flex: '0 0 auto', py: 1 }}> 
              <LinearProgress variant="determinate" sx={{ width: '125px', height: '8px' }} value={props.File.UploadProgress} />
            </Box>

            {/* Bottom */}
            <Box sx={{ alignSelf: 'center', textAlign: 'center', flex: '1 1 auto', pt: 1 }}>
              <Typography component="h6" sx={{ color: theme.palette.text.secondary, fontSize: '.8rem' }}>
                {props.File.UploadProgress}%
              </Typography>
            </Box>
          </Box>
        }

        {/* Preprocessing - category label, file size, file name, delete button, bg color to visually distinguish  */}
        {props.File?.FileStatus === FileStatus.Processing && 
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              width:'100%',
              height:'100%', 
              border: '1px solid',
              borderColor: theme.palette.grey[400],
              borderRadius: 4
            }}>

            {/* Top */}
            <Box sx={{ alignSelf: 'center', flex: '1 1 auto', display: 'flex', flexDirection: 'column-reverse', pb: 1 }}>        
              <Chip label={'snapshot'} size='small' sx={{}} />
            </Box>

            {/* Middle */}
            <Box sx={{ alignSelf: 'center', textAlign: 'center', flex: '0 0 auto' }}> 
              <Tooltip title="Ingesting file to Bronze.">
                <HourglassTopIcon fontSize='large' sx={{ color: theme.palette.text.secondary}}/>
              </Tooltip>
            </Box>

            {/* Bottom */}
            <Box sx={{ alignSelf: 'center', textAlign: 'center', flex: '1 1 auto' }}>
              <Typography component="h6" sx={{ color: theme.palette.text.secondary, fontSize: '.8rem' }}>
                Bronze ingesting...
              </Typography>
            </Box>
          </Box>
        }

        {/* Uploaded - allow replacing the existing snapshot file with a new upload */}
        {props.File?.FileStatus === FileStatus.Uploaded && 
          <Dropzone
            file={props.File}
            multiple={false}
            accept={{
              'text/csv': ['.csv'],
              'application/x-spss-sav': ['.sav'],
              'application/octet-stream': ['.sav']
            }}
            fileRequirementsHelp='Upload a replacement snapshot file to reprocess this snapshot.'
            fileRequirementsMessage='CSV/SAV'
            onDrop={onDrop}
          />
        }

        {/* Error - allow re-upload of a replacement file to retry processing */}
        {props.File?.FileStatus === FileStatus.Error && 
          <Dropzone
            file={props.File}
            multiple={false}
            accept={{
              'text/csv': ['.csv'],
              'application/x-spss-sav': ['.sav'],
              'application/octet-stream': ['.sav']
            }}
            fileRequirementsHelp='Upload a replacement file to retry this snapshot.'
            fileRequirementsMessage='CSV/SAV'
            onDrop={onDrop}
          />
        }

      </Box>
    </> 
  );
}